import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { apiService, CalendarResponse, InvestmentRecord as ApiInvestmentRecord } from '../services/api';
import { CalendarDay, InvestmentRecord } from '../types';
import { useAuth } from './useAuth';
import { localStorageUtils } from '../utils/localStorage';
import { convertToYahooTicker, getStockPriceHistory } from '../utils/stockDataService';
import { getApiFailureMessage, getUnifiedApiErrorMessage } from '../utils/apiErrorMessage';

// React StrictMode(개발)에서 동일 effect가 2번 실행될 수 있으므로,
// 로그인 상태에서의 `fetchCalendarData(year, month)`는 (year,month) 단위로
// 요청/캐시를 공유해 중복 네트워크를 줄인다.
type CalendarApiCache = {
  calendarData: CalendarResponse;
  calendarDays: CalendarDay[];
  fetchedAt: number;
};

const CALENDAR_TTL_MS = 1500;
const calendarInFlightByKey = new Map<string, Promise<CalendarApiCache>>();
const calendarCacheByKey = new Map<string, CalendarApiCache>();

const mergeCalendarDaysByDate = (prev: CalendarDay[], incoming: CalendarDay[]): CalendarDay[] => {
  const byDate = new Map<string, CalendarDay>();
  prev.forEach(day => byDate.set(format(day.date, 'yyyy-MM-dd'), day));
  incoming.forEach(day => byDate.set(format(day.date, 'yyyy-MM-dd'), day));
  return Array.from(byDate.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
};

export interface CalendarState {
  calendarData: CalendarResponse | null;
  calendarDays: CalendarDay[];
  selectedDateRecords: ApiInvestmentRecord[];
  isLoading: boolean;
  error: string | null;
}

export interface CalendarActions {
  fetchCalendarData: (year: number, month: number) => Promise<void>;
  fetchDateRecords: (date: string) => Promise<void>;
  clearError: () => void;
}

export const useCalendar = (): CalendarState & CalendarActions => {
  const { isAuthenticated } = useAuth();
  const [calendarData, setCalendarData] = useState<CalendarResponse | null>(null);
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
  const [selectedDateRecords, setSelectedDateRecords] = useState<ApiInvestmentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 캘린더 데이터 조회
  const fetchCalendarData = useCallback(async (year: number, month: number) => {
    try {
      setIsLoading(true);
      setError(null);

      if (isAuthenticated) {
        const cacheKey = `${year}-${month}`;
        const now = Date.now();

        const cached = calendarCacheByKey.get(cacheKey);
        if (cached && now - cached.fetchedAt < CALENDAR_TTL_MS) {
          setCalendarData(cached.calendarData);
          setCalendarDays(prev => mergeCalendarDaysByDate(prev, cached.calendarDays));
          setIsLoading(false);
          return;
        }

        const inFlight = calendarInFlightByKey.get(cacheKey);
        if (inFlight) {
          const result = await inFlight;
          setCalendarData(result.calendarData);
          setCalendarDays(prev => mergeCalendarDaysByDate(prev, result.calendarDays));
          setIsLoading(false);
          return;
        }

        const inflightPromise: Promise<CalendarApiCache> = (async () => {
          const response = await apiService.getCalendarData(year, month);
          if (!response.success || !response.data) {
            throw new Error(getUnifiedApiErrorMessage(response));
          }

          const convertedDays: CalendarDay[] = (response.data.days || []).map(day => {
            const [y, m, d] = day.date.split('-').map(Number);
            return {
              date: new Date(y, m - 1, d),
              profitRate: day.profitRate !== undefined ? day.profitRate : undefined,
              profitRateChange: day.profitRateChange !== undefined ? day.profitRateChange : undefined,
              isHoliday: day.isHoliday || false,
              canCalculate: day.canCalculate !== undefined ? day.canCalculate : true,
              hasInvestment: day.hasInvestment || false,
              records: []
            };
          });

          return {
            calendarData: response.data,
            calendarDays: convertedDays,
            fetchedAt: Date.now(),
          };
        })();

        calendarInFlightByKey.set(cacheKey, inflightPromise);
        try {
          const result = await inflightPromise;
          calendarCacheByKey.set(cacheKey, result);
          setCalendarData(result.calendarData);
          setCalendarDays(prev => mergeCalendarDaysByDate(prev, result.calendarDays));
        } finally {
          calendarInFlightByKey.delete(cacheKey);
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에서 조회
        const localInvestments = localStorageUtils.getInvestments();
        
        // 해당 월의 날짜 범위
        const monthStart = new Date(year, month - 1, 1);
        const monthEnd = new Date(year, month, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // 해당 월에 거래된 기록 (hasInvestment 마킹용)
        const monthInvestments = localInvestments.filter(inv => {
          if (!inv.date) return false;
          const invDate = new Date(inv.date);
          return invDate >= monthStart && invDate <= monthEnd;
        });
        
        // 날짜별로 그룹화
        const dateMap = new Map<string, any[]>();
        monthInvestments.forEach(inv => {
          const dateStr = inv.date || inv.recordDate;
          if (!dateStr) return;
          if (!dateMap.has(dateStr)) dateMap.set(dateStr, []);
          dateMap.get(dateStr)!.push(inv);
        });
        
        // 이 달에 보유 가능한 모든 종목 수집 (이전 달 매수 포함)
        const allTickers = new Map<string, string>(); // stockCode → ticker
        localInvestments.forEach(inv => {
          if (inv.stockCode && !allTickers.has(inv.stockCode)) {
            allTickers.set(inv.stockCode, inv.ticker || convertToYahooTicker(inv.stockCode));
          }
        });
        
        // 히스토리 조회 기간: 월 시작 -10일 ~ 오늘 (전일 종가 확보 위해 시작 전 여분 포함)
        const histStartDate = new Date(year, month - 1, 1);
        histStartDate.setDate(histStartDate.getDate() - 10);
        const histStart = format(histStartDate, 'yyyy-MM-dd');
        const histEnd = format(today < monthEnd ? today : monthEnd, 'yyyy-MM-dd');
        
        // 각 종목의 일별 종가 히스토리를 병렬로 가져오기
        // Map<stockCode, Map<date, closePrice>>
        const priceHistoryCache = new Map<string, Record<string, number>>();
        
        if (allTickers.size > 0) {
          await Promise.all(
            Array.from(allTickers.entries()).map(async ([stockCode, ticker]) => {
              const history = await getStockPriceHistory(ticker, histStart, histEnd);
              if (Object.keys(history).length > 0) {
                priceHistoryCache.set(stockCode, history);
              }
            })
          );
        }
        
        // 히스토리에 있는 날짜 목록을 정렬 (전일 종가 탐색용)
        const allHistoryDates = new Set<string>();
        priceHistoryCache.forEach(history => {
          Object.keys(history).forEach(d => allHistoryDates.add(d));
        });
        const sortedHistoryDates = Array.from(allHistoryDates).sort();
        
        // 특정 날짜 이전의 마지막 거래일 찾기
        const getPrevTradingDate = (dateStr: string): string | null => {
          const idx = sortedHistoryDates.indexOf(dateStr);
          if (idx > 0) return sortedHistoryDates[idx - 1];
          // 히스토리에 없는 경우 해당 날짜 이전의 마지막 날짜 탐색
          const before = sortedHistoryDates.filter(d => d < dateStr);
          return before.length > 0 ? before[before.length - 1] : null;
        };
        
        // CalendarDay 배열 생성
        const daysInMonth = monthEnd.getDate();
        const convertedDays: CalendarDay[] = [];
        
        for (let day = 1; day <= daysInMonth; day++) {
          const date = new Date(year, month - 1, day);
          const dateStr = format(date, 'yyyy-MM-dd');
          const rawRecords = dateMap.get(dateStr) || [];
          
          // 투자 기록 변환
          const records = rawRecords.map((inv: any) => ({
            id: String(inv.id || Date.now()),
            date: inv.date || inv.recordDate || dateStr,
            type: (inv.type === 'BUY' ? 'buy' : 'sell') as 'buy' | 'sell',
            stockName: inv.stockName || '',
            investmentRatio: inv.investmentRatio || 0,
            quantity: inv.quantity,
            buyReason: inv.buyReason,
            buyPrice: inv.pricePerShare,
            dividend: inv.dividendPerShare,
            sellReason: inv.sellReason,
            sellQuantity: inv.sellQuantity,
            sellRatio: inv.sellRatio,
            realizedProfitRate: inv.realizedProfitRate,
            selectedStockId: inv.selectedStockId ? String(inv.selectedStockId) : undefined
          }));
          
          // 미래 날짜 또는 주말 → 표시 없음
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const isFuture = date > today;
          
          if (isWeekend || isFuture) {
            convertedDays.push({ date, canCalculate: false, hasInvestment: records.length > 0, records });
            continue;
          }
          
          // 해당 날짜가 실제 거래일인지 확인 (히스토리에 데이터가 있는 날짜)
          let isTraddingDay = false;
          Array.from(priceHistoryCache.values()).forEach(history => {
            if (history[dateStr] != null) isTraddingDay = true;
          });
          
          // 해당 날짜까지의 보유 종목 계산
          const recordsUpToDate = localInvestments.filter(inv => {
            if (!inv.date && !inv.recordDate) return false;
            const invDate = new Date(inv.date || inv.recordDate);
            return invDate <= date && !inv.isDeleted;
          });
          
          // 종목별 보유 수량 및 평균 단가 계산
          const stockMap = new Map<string, {
            totalQuantity: number;
            totalAmount: number;
            averagePrice: number;
            stockCode: string;
          }>();
          
          recordsUpToDate.forEach(inv => {
            if (!inv.stockCode || inv.type !== 'BUY') return;
            const key = inv.stockCode;
            if (!stockMap.has(key)) {
              stockMap.set(key, { totalQuantity: 0, totalAmount: 0, averagePrice: 0, stockCode: key });
            }
            const stock = stockMap.get(key)!;
            if (inv.pricePerShare && inv.quantity) {
              stock.totalQuantity += inv.quantity;
              stock.totalAmount += inv.pricePerShare * inv.quantity;
            }
          });
          
          // 매도 수량 차감
          recordsUpToDate.forEach(inv => {
            if (inv.type === 'SELL' && inv.selectedStockId) {
              const buyRecord = recordsUpToDate.find(r => r.id === inv.selectedStockId);
              if (buyRecord?.stockCode) {
                const stock = stockMap.get(buyRecord.stockCode);
                if (stock) {
                  stock.totalQuantity -= (inv.sellQuantity || inv.quantity || 0);
                  if (stock.totalQuantity < 0) stock.totalQuantity = 0;
                }
              }
            }
          });
          
          // 평균 단가 계산
          stockMap.forEach(stock => {
            if (stock.totalQuantity > 0) {
              stock.averagePrice = stock.totalAmount / stock.totalQuantity;
            }
          });
          
          // 실제 보유 종목 (수량 > 0)
          const holdings = Array.from(stockMap.values()).filter(s => s.totalQuantity > 0 && s.averagePrice > 0);
          const hasHoldings = holdings.length > 0;
          
          // 거래일이 아니거나 보유 종목 없으면 → canCalculate: false
          if (!hasHoldings || !isTraddingDay) {
            convertedDays.push({
              date,
              canCalculate: false,
              hasInvestment: records.length > 0,
              records
            });
            continue;
          }
          
          // 전일 거래일 탐색
          const prevTradingDate = getPrevTradingDate(dateStr);
          
          // 수익률 및 전일 대비 변동률 계산 (가중 평균)
          let totalProfitRateChange = 0;
          let totalChangeWeight = 0;
          let totalProfitRate = 0;
          let totalProfitRateWeight = 0;
          
          holdings.forEach(stock => {
            const history = priceHistoryCache.get(stock.stockCode);
            if (!history) return;
            
            const closePrice = history[dateStr];
            if (closePrice == null || closePrice <= 0) return;
            
            const weight = stock.totalQuantity * stock.averagePrice;
            
            // 누적 수익률 = (현재가 - 평균단가) / 평균단가 * 100
            const profitRate = ((closePrice - stock.averagePrice) / stock.averagePrice) * 100;
            totalProfitRate += profitRate * weight;
            totalProfitRateWeight += weight;
            
            // 전일 대비 변동률 = (오늘 종가 - 전일 종가) / 평균단가 * 100
            if (prevTradingDate) {
              const prevClose = history[prevTradingDate];
              if (prevClose != null && prevClose > 0) {
                const dailyChange = ((closePrice - prevClose) / stock.averagePrice) * 100;
                totalProfitRateChange += dailyChange * weight;
                totalChangeWeight += weight;
              }
            }
          });
          
          const profitRate = totalProfitRateWeight > 0 ? totalProfitRate / totalProfitRateWeight : undefined;
          const profitRateChange = totalChangeWeight > 0 ? totalProfitRateChange / totalChangeWeight : undefined;
          
          convertedDays.push({
            date,
            profitRate,
            profitRateChange,
            canCalculate: profitRateChange !== undefined,
            hasInvestment: records.length > 0,
            records
          });
        }
        
        console.log('로컬스토리지 캘린더 데이터:', {
          year, month,
          daysCount: convertedDays.length,
          tradingDaysWithChange: convertedDays.filter(d => d.profitRateChange !== undefined).length
        });
        
        setCalendarDays(prev => mergeCalendarDaysByDate(prev, convertedDays));
      }
    } catch (error: any) {
      console.error('캘린더 데이터 조회 오류:', error);
      setError(getApiFailureMessage(error, '캘린더 데이터를 불러오는 중 오류가 발생했습니다.'));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // 특정 날짜의 투자 기록 조회
  const fetchDateRecords = useCallback(async (date: string) => {
    try {
      setIsLoading(true);
      setError(null);

      if (isAuthenticated) {
        // 로그인한 경우: DB에서 조회
        const response = await apiService.getCalendarRecords(date);
        
        if (response.success && response.data) {
          setSelectedDateRecords(response.data);
        } else {
          setError(getUnifiedApiErrorMessage(response));
          setSelectedDateRecords([]);
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에서 조회
        const localInvestments = localStorageUtils.getInvestments();
        const rawRecords = localInvestments.filter(inv => {
          const invDate = inv.date || inv.recordDate;
          return invDate === date;
        });
        
        // api.ts의 InvestmentRecord 형식으로 변환
        const dateRecords: ApiInvestmentRecord[] = rawRecords.map((inv: any) => ({
          id: inv.id || Date.now(),
          recordDate: inv.date || inv.recordDate || date,
          type: (inv.type || 'BUY') as 'BUY' | 'SELL',
          stockName: inv.stockName || '',
          stockCode: inv.stockCode,
          investmentRatio: inv.investmentRatio || 0,
          quantity: inv.quantity,
          pricePerShare: inv.pricePerShare,
          totalAmount: inv.totalAmount,
          dividendPerShare: inv.dividendPerShare,
          dividendRatio: inv.dividendRatio,
          buyReason: inv.buyReason,
          sellReason: inv.sellReason,
          sellQuantity: inv.sellQuantity,
          sellRatio: inv.sellRatio,
          realizedProfitRate: inv.realizedProfitRate,
          realizedProfitAmount: inv.realizedProfitAmount,
          selectedStockId: inv.selectedStockId,
          currentPrice: inv.currentPrice,
          unrealizedProfitRate: inv.unrealizedProfitRate,
          unrealizedProfitAmount: inv.unrealizedProfitAmount,
          isDeleted: inv.isDeleted || false,
          createdAt: inv.createdAt || new Date().toISOString(),
          updatedAt: inv.updatedAt || new Date().toISOString()
        }));
        
        setSelectedDateRecords(dateRecords);
        console.log('로컬스토리지 날짜별 기록:', date, dateRecords.length, '개');
      }
    } catch (error: any) {
      console.error('투자 기록 조회 오류:', error);
      setError(getApiFailureMessage(error, '투자 기록을 불러오는 중 오류가 발생했습니다.'));
      setSelectedDateRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // 에러 초기화
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // 현재 년월로 초기 데이터 로드 (인증된 경우에만)
  useEffect(() => {
    // 인증 상태는 외부에서 확인해야 하므로, 여기서는 호출하지 않음
    // App.tsx에서 인증된 경우에만 호출하도록 변경
  }, []);

  return {
    calendarData,
    calendarDays,
    selectedDateRecords,
    isLoading,
    error,
    fetchCalendarData,
    fetchDateRecords,
    clearError
  };
};
